/**
 * Lists of emails blocked in various contexts and relevant functions.
 */

const freeEmailDomains = [
  '163.com',
  'aim.com',
  'alice.it',
  'aliceadsl.fr',
  'aol.com',
  'arcor.de',
  'att.net',
  'bellsouth.net',
  'bigpond.com',
  'bigpond.net.au',
  'bluewin.ch',
  'blueyonder.co.uk',
  'bol.com.br',
  'centurytel.net',
  'charter.net',
  'chello.nl',
  'club-internet.fr',
  'comcast.net',
  'cox.net',
  'earthlink.net',
  'facebook.com',
  'free.fr',
  'freenet.de',
  'frontiernet.net',
  'gmail.com',
  'gmx.de',
  'gmx.net',
  'googlemail.com',
  'hetnet.nl',
  'home.nl',
  'hotmail.co.uk',
  'hotmail.com',
  'hotmail.de',
  'hotmail.es',
  'hotmail.fr',
  'hotmail.it',
  'icloud.com',
  'ig.com.br',
  'juno.com',
  'laposte.net',
  'libero.it',
  'live.ca',
  'live.co.uk',
  'live.com',
  'live.com.au',
  'live.fr',
  'live.it',
  'live.nl',
  'mac.com',
  'mail.com',
  'mail.ru',
  'me.com',
  'msn.com',
  'neuf.fr',
  'ntlworld.com',
  'optonline.net',
  'optusnet.com.au',
  'orange.fr',
  'outlook.com',
  'planet.nl',
  'qq.com',
  'rambler.ru',
  'rediffmail.com',
  'rocketmail.com',
  'sbcglobal.net',
  'sfr.fr',
  'shaw.ca',
  'sky.com',
  'skynet.be',
  'sympatico.ca',
  't-online.de',
  'telenet.be',
  'terra.com.br',
  'tin.it',
  'tiscali.co.uk',
  'tiscali.it',
  'uol.com.br',
  'verizon.net',
  'virgilio.it',
  'voila.fr',
  'wanadoo.fr',
  'web.de',
  'windstream.net',
  'yahoo.ca',
  'yahoo.co.id',
  'yahoo.co.in',
  'yahoo.co.jp',
  'yahoo.co.uk',
  'yahoo.com',
  'yahoo.com.ar',
  'yahoo.com.au',
  'yahoo.com.br',
  'yahoo.com.mx',
  'yahoo.com.sg',
  'yahoo.de',
  'yahoo.es',
  'yahoo.fr',
  'yahoo.in',
  'yahoo.it',
  'yandex.ru',
  'ymail.com',
  'zonnet.nl',
];

/**
 * Check if email belongs to free email providers.
 * Assumes a valid email address.
 *
 * @param email A valid email address
 * @returns true if it´s a free email provider.
 */
export function isFreeEmailDomain(email: string) {
  const [, domain] = email.trim().split('@');
  if (freeEmailDomains.includes(domain.toLowerCase())) {
    return true;
  }
  return false;
}

const trashEmailDomains = [
  '10minutemail.com',
  'getnada.com',
  'guerrillamail.com',
  'hh7f.com',
  'mailinator.com',
  'rtsos.com',
  'temp-mail.org',
  'throwawaymail.com',
  'trash-mail.com',
  'yopmail.com',
];

export function isTrashEmailDomain(email: string) {
  const [, domain] = email.trim().split('@');
  if (trashEmailDomains.includes(domain.toLowerCase())) {
    return true;
  }
  return false;
}

const competitorEmailDomains = [
  'apiday.com',
  'atlasmetrics.io',
  'celsia.io',
  'coolset.com',
  'daato.co',
  'datamaran.com',
  'datia.app',
  'diginex.com',
  'ecobio.com',
  'esgbook.com',
  'gistimpact.com',
  'greenomy.io',
  'impaakt.com',
  'impactcubed.com',
  'inrate.com',
  'issgovernance.com',
  'novisto.com',
  'plana.earth',
  'positiongreen.com',
  'sprih.com',
  'squareone.no',
  'stratsys.com',
  'sustashift.com',
  'wellfish.se',
  'worldfavor.com',
];

export function isCompetitorEmailDomain(email: string) {
  const [, domain] = email.trim().split('@');
  if (competitorEmailDomains.includes(domain.toLowerCase())) {
    return true;
  }

  if (competitorEmailDomains.some(blocked => domain.toLowerCase().endsWith('.' + blocked))) {
    return true;
  }

  return false;
}

/** Upright's own corporate email domain. */
export const UPRIGHT_EMAIL_DOMAIN = 'uprightproject.com';

export function hasBlockedEmailAlias(email: string): boolean {
  const [localPart, domain] = email.trim().split('@');

  // Allow + aliases for Upright domain
  if (domain.toLowerCase() === UPRIGHT_EMAIL_DOMAIN) {
    return false;
  }

  // Block + aliases for all other domains
  return localPart.includes('+');
}

/**
 * Whether the address belongs to Upright itself.
 *
 * The domain must match exactly: look-alikes (`x@notuprightproject.com`, and the
 * mistyped `x@uprighproject.com`) and subdomains (`x@mail.uprightproject.com`) are
 * rejected, since anyone can register those and receive the account's verification
 * and password-reset mail. The local part is unconstrained, so both plain
 * `first.last@` addresses and the `first.last+label@` aliases used for test accounts
 * pass.
 */
export function isUprightEmail(email: string): boolean {
  const normalized = email.trim().toLowerCase();
  const domainStart = normalized.lastIndexOf('@');
  // Also rejects an address with no local part (`@uprightproject.com`).
  if (domainStart <= 0) {
    return false;
  }
  return normalized.slice(domainStart + 1) === UPRIGHT_EMAIL_DOMAIN;
}
